import { savedListTypes } from '../utils';

export const createList = (axiosInstance, listName, listType, playerSchoolId) => {
    const url = playerSchoolId ? `v2/saved` : `v1/saved`;
    let postParams = {
        name: listName,
        type: listType === 'players' ? 'Player' : 'School',
    };
    // if we need to create list and players in a single api call
    if (playerSchoolId) {
        postParams = {
            ...postParams,
            listId: 0,
            playerOrSchoolIds: playerSchoolId,
        };
    }
    return axiosInstance.post(url, postParams);
};

export const getAllLists = (axiosInstance) => {
    const url = `v1/saved`;
    return axiosInstance.get(url);
};

export const checkIfListExists = (axiosInstance, listName, listType) => {
    const url = `v1/saved/exists`;
    const postParams = {
        name: listName,
        type: listType === 'players' ? 'Player' : 'School',
    };
    return axiosInstance.post(url, postParams);
};

export const deleteList = (axiosInstance, listId) => {
    const url = `v1/saved/${listId}`;
    return axiosInstance.delete(url);
};

export const updateListName = (axiosInstance, listId, listname) => {
    const url = `v1/saved/${listId}/updatelistname`;
    const postParams = {
        name: listname,
    };
    return axiosInstance.post(url, postParams);
};

export const addPlayerToList = (axiosInstance, playerId, listId) => {
    const url = `v1/saved/addplayertolist`;
    const postParams = {
        playerId,
        listId,
    };
    return axiosInstance.post(url, postParams);
};

export const removePlayerFromList = (axiosInstance, playerId, listId) => {
    const url = `v1/saved/removeplayerfromlist`;
    const postParams = {
        playerId,
        listId,
    };
    return axiosInstance.post(url, postParams);
};

export const addSchoolToList = (axiosInstance, schoolClubId, listId) => {
    const url = `v1/saved/addschooltolist`;
    const postParams = {
        schoolClubId,
        listId,
    };
    return axiosInstance.post(url, postParams);
};

export const removeSchoolFromList = (axiosInstance, schoolClubId, listId) => {
    const url = `v1/saved/removeschoolfromlist`;
    const postParams = {
        schoolClubId,
        listId,
    };
    return axiosInstance.post(url, postParams);
};

export const updateItemNotes = (axiosInstance, savedId, notes, listType) => {
    let url = '';
    if (listType === savedListTypes.players.key) {
        url = `v1/saved/${savedId}/updateplayernotes`;
    } else {
        url = `v1/saved/${savedId}/updateschoolnotes`;
    }
    const postParams = {
        notes,
    };
    return axiosInstance.post(url, postParams);
};

export const getSavedPlayerAndLists = (axiosInstance, playerId) => {
    const url = `v1/saved/getplayerandlists`;
    return axiosInstance.get(url, {
        params: {
            playerId,
        },
    });
};

export const getSavedSchoolsAndLists = (axiosInstance, schoolClubId) => {
    const url = `v1/saved/getschoolandlists`;
    return axiosInstance.get(url, {
        params: {
            schoolClubId,
        },
    });
};

export const createPlayerWithLists = (axiosInstance, listIds, playerId) => {
    const url = `v1/saved/createplayerwithlists`;
    const postParams = {
        listIds,
        playerId,
    };
    return axiosInstance.post(url, postParams);
};

export const updatePlayerLists = (axiosInstance, listIds, savedId) => {
    const url = `v1/saved/${savedId}/updateplayerlists`;
    const postParams = {
        listIds,
    };
    return axiosInstance.post(url, postParams);
};

export const createSchoolWithLists = (axiosInstance, listIds, schoolClubId) => {
    const url = `v1/saved/createschoolwithlists`;
    const postParams = {
        listIds,
        schoolClubId,
    };
    return axiosInstance.post(url, postParams);
};

export const updateSchoolLists = (axiosInstance, listIds, savedId) => {
    const url = `v1/saved/${savedId}/updateschoollists`;
    const postParams = {
        listIds,
    };
    return axiosInstance.post(url, postParams);
};

export const getNotificationPreferences = (axiosInstance) => {
    const url = `v1/savedplayerlist/notificationpreferences`;
    return axiosInstance.get(url);
};

export const getPlayerSavedStatus = (axiosInstance, playerId) => {
    const url = `v1/savedplayerlist/players/${playerId}`;
    return axiosInstance.get(url);
};

// Batch saved-status (TMS-2282). Sparse response — `players` carries an entry only for ids the member
// has saved; any requested id absent from it is not saved. Callers hydrate a page-delta of ids per call.
export const getPlayersSavedStatus = (axiosInstance, playerIds) => {
    const url = `v1/savedplayerlist/players/saved-status`;
    return axiosInstance.post(url, { playerIds });
};

export const updateNotificationPreferences = (axiosInstance, notificationsEnabled) => {
    const url = `v1/savedplayerlist/notificationpreferences`;
    return axiosInstance.put(url, { notificationsEnabled });
};

export const getPlayerLists = (axiosInstance) => {
    const url = `v1/savedplayerlist/lists`;
    return axiosInstance.get(url);
};

export const createPlayerList = (axiosInstance, name) => {
    const url = `v1/savedplayerlist/lists`;
    return axiosInstance.post(url, { name });
};

export const getListDetail = (axiosInstance, listId) => {
    const url = `v1/savedplayerlist/lists/${listId}`;
    return axiosInstance.get(url);
};

export const renameList = (axiosInstance, listId, name) => {
    const url = `v1/savedplayerlist/lists/${listId}`;
    return axiosInstance.put(url, { name });
};

export const savePlayersToLists = (axiosInstance, { playerIds, listIds, newListName }) => {
    const url = `v1/savedplayerlist/players`;
    const postParams = {
        playerIds,
        listIds,
        newListName: newListName || null,
    };
    return axiosInstance.post(url, postParams);
};

export const removePlayerFromAllLists = (axiosInstance, playerId) => {
    const url = `v1/savedplayerlist/players/${playerId}`;
    return axiosInstance.delete(url);
};

// Named to avoid colliding with the legacy player-scoped `removePlayerFromList`
// (v1/saved/removeplayerfromlist) above; this is the list-scoped TMS-2175 surface.
export const removeSavedListPlayer = (axiosInstance, listId, playerId) => {
    const url = `v1/savedplayerlist/lists/${listId}/players/${playerId}`;
    return axiosInstance.delete(url);
};

export const searchPlayerLists = (axiosInstance, { search, orderBy, orderByRules, page, size }) => {
    const url = `v1/savedplayerlist/lists/search`;
    return axiosInstance.get(url, {
        params: {
            search: search || undefined,
            orderBy: orderBy || undefined,
            orderByRules: orderByRules || undefined,
            page,
            size,
        },
    });
};

export const deletePlayerList = (axiosInstance, listId) => {
    const url = `v1/savedplayerlist/lists/${listId}`;
    return axiosInstance.delete(url);
};

export const searchAddPlayers = (axiosInstance, { query, page, size, cancelToken } = {}) => {
    const url = `v1/savedplayerlist/players/search`;
    const config = {
        params: {
            query,
            page,
            size,
        },
    };
    if (cancelToken) {
        config.cancelToken = cancelToken;
    }
    return axiosInstance.get(url, config);
};

export const searchListPlayers = (axiosInstance, { listId, search, orderBy, orderByRules, page, size, viewedAsOf }) => {
    const url = `v1/savedplayerlist/lists/${listId}/players/search`;
    return axiosInstance.get(url, {
        params: {
            search: search || undefined,
            orderBy: orderBy || undefined,
            orderByRules: orderByRules || undefined,
            page,
            size,
            viewedAsOf: viewedAsOf || undefined,
        },
    });
};

export const markListViewed = (axiosInstance, listId) => {
    const url = `v1/savedplayerlist/lists/${listId}/viewed`;
    return axiosInstance.post(url);
};

// Per-player note (TMS-2176). Stored on SavedPlayer.Notes, so it is cross-list — the note shows on the
// player's row on every one of the member's lists. An empty string clears it server-side.
export const updatePlayerNote = (axiosInstance, playerId, note) => {
    const url = `v1/savedplayerlist/players/${playerId}/note`;
    return axiosInstance.put(url, { note });
};
